import React, { useState, useEffect, useCallback, useRef } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import PerformanceDashboard from './PerformanceDashboard';
import AnalyticsDashboard from './AnalyticsDashboard';
import BotDetectionTab from './components/BotDetectionTab';

// ─── Constants ───────────────────────────────────────────────
const PARAMS = [
  { key: 'trading_fee_percentage', label: 'Trading Fee %', min: 0.1, max: 5, step: 0.1, unit: '%', dbKey: 'trading_fee_multiplier' },
  { key: 'leverage_cap_multiplier', label: 'Leverage Cap', min: 1.5, max: 5, step: 0.1, unit: 'x', dbKey: 'leverage_cap' },
  { key: 'crime_base_success_rate', label: 'Crime Base Success Rate', min: 5, max: 50, step: 1, unit: '%', dbKey: 'crime_reward_multiplier' },
  { key: 'business_operating_cost_multiplier', label: 'Business Operating Cost', min: 0.5, max: 2.0, step: 0.05, unit: 'x', dbKey: 'operating_cost_multiplier' },
  { key: 'dividend_yield_multiplier', label: 'Dividend Yield', min: 0.5, max: 2.0, step: 0.05, unit: 'x', dbKey: 'economy_modifier' },
  { key: 'loan_interest_rate', label: 'Loan Interest Rate', min: 1, max: 20, step: 0.5, unit: '%', dbKey: 'trading_fee_multiplier' },
  { key: 'inflation_trigger_threshold', label: 'Inflation Trigger Threshold', min: 10, max: 30, step: 1, unit: '%', dbKey: 'economy_modifier' },
];

const EMERGENCY_CONTROLS = [
  { key: 'freeze_ipo', label: 'Freeze IPO', desc: 'Disables all new IPO launches', icon: 'Pause', color: 'text-warning', bg: 'bg-warning/10 border-warning/20', stateKey: 'ipo_frozen' },
  { key: 'disable_margin', label: 'Disable Margin Trading', desc: 'Sets margin_enabled=false globally', icon: 'XCircle', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', stateKey: 'margin_disabled' },
  { key: 'force_recession', label: 'Force Recession State', desc: 'Sets economy_modifier=0.7', icon: 'TrendingDown', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', stateKey: 'recession_state' },
  { key: 'increase_trading_fees', label: 'Increase Trading Fees +50%', desc: 'Emergency fee hike', icon: 'DollarSign', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20', stateKey: null },
];

const SEVERITY_COLORS = {
  LOW: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  MEDIUM: 'bg-warning/10 text-warning border-warning/20',
  HIGH: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  CRITICAL: 'bg-red-500/10 text-red-400 border-red-500/20',
  profit_spike: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  trade_frequency: 'bg-warning/10 text-warning border-warning/20',
  wash_trading: 'bg-red-500/10 text-red-400 border-red-500/20',
  crime_farming: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  net_worth_slope: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  multi_account: 'bg-red-500/10 text-red-400 border-red-500/20',
  margin_abuse: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  vault_siphon: 'bg-red-500/10 text-red-400 border-red-500/20',
  timestamp_spoof: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  bot_precision: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
};

// ─── Inflation Gauge ─────────────────────────────────────────
const InflationGauge = ({ value }) => {
  const pct = Math.min(Math.abs(value), 100);
  const color = value < 5 ? '#22c55e' : value < 15 ? '#f59e0b' : value < 25 ? '#f97316' : '#ef4444';
  const label = value < 5 ? 'SAFE' : value < 15 ? 'CAUTION' : value < 25 ? 'WARNING' : 'CRITICAL';
  const textColor = value < 5 ? 'text-success' : value < 15 ? 'text-warning' : value < 25 ? 'text-orange-400' : 'text-red-400';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">InflationIndex</span>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${SEVERITY_COLORS?.[label] || 'bg-surface border-border text-foreground'}`}>{label}</span>
          <span className={`text-lg font-mono font-bold ${textColor}`}>{value?.toFixed(2)}%</span>
        </div>
      </div>
      <div className="w-full h-3 bg-surface rounded-full overflow-hidden border border-border">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span className="text-success">0% Safe</span>
        <span className="text-warning">5%</span>
        <span className="text-orange-400">15%</span>
        <span className="text-red-400">25%+ Critical</span>
      </div>
    </div>
  );
};

// ─── Tick Countdown ──────────────────────────────────────────
const TickCountdown = ({ nextTickAt }) => {
  const [secs, setSecs] = useState(60);
  useEffect(() => {
    if (!nextTickAt) return;
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(nextTickAt) - new Date()) / 1000));
      setSecs(diff);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [nextTickAt]);
  return (
    <div className="text-center">
      <p className="text-3xl font-mono font-bold text-primary">{secs}s</p>
      <p className="text-xs text-muted-foreground">Next Tick</p>
    </div>
  );
};

// ─── User Details Modal ──────────────────────────────────────
const UserDetailsModal = ({ userId, username, onClose }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase?.rpc('get_user_suspicious_activities', { p_target_user_id: userId });
      setActivities(data?.activities || []);
      setLoading(false);
    };
    fetch();
  }, [userId]);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground">Suspicious Activities</h2>
            <p className="text-sm text-muted-foreground">{username}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface rounded-lg"><Icon name="X" size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Icon name="Loader" size={24} className="animate-spin text-primary" /></div>
          ) : activities?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No suspicious activities found</p>
          ) : activities?.map(a => (
            <div key={a?.id} className={`p-3 rounded-lg border ${SEVERITY_COLORS?.[a?.flag_type] || 'bg-surface border-border'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold uppercase">{a?.flag_type?.replace(/_/g, ' ')}</span>
                <span className={`text-xs px-2 py-0.5 rounded border ${
                  a?.flag_status === 'pending' ? 'bg-warning/10 text-warning border-warning/20' :
                  a?.flag_status === 'cleared' ? 'bg-success/10 text-success border-success/20' : 'bg-red-500/10 text-red-400 border-red-500/20'
                }`}>{a?.flag_status?.toUpperCase()}</span>
              </div>
              <p className="text-xs text-muted-foreground">Risk Score: <span className="font-mono text-foreground">{Number(a?.risk_score)?.toFixed(0)}</span></p>
              {a?.z_score && <p className="text-xs text-muted-foreground">Z-Score: <span className="font-mono">{Number(a?.z_score)?.toFixed(2)}</span></p>}
              {a?.details && Object.keys(a?.details)?.length > 0 && (
                <pre className="text-xs text-muted-foreground mt-2 bg-background/50 p-2 rounded overflow-x-auto">{JSON.stringify(a?.details, null, 2)}</pre>
              )}
              <p className="text-xs text-muted-foreground mt-1">{new Date(a?.created_at)?.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Audit History Modal ─────────────────────────────────────
const AuditHistoryModal = ({ userId, username, onClose }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase?.rpc('get_user_audit_history', { p_target_user_id: userId });
      setActions(data?.actions || []);
      setLoading(false);
    };
    fetch();
  }, [userId]);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground">Admin Action History</h2>
            <p className="text-sm text-muted-foreground">{username}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface rounded-lg"><Icon name="X" size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Icon name="Loader" size={24} className="animate-spin text-primary" /></div>
          ) : actions?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No admin actions recorded</p>
          ) : actions?.map(a => (
            <div key={a?.id} className="p-3 rounded-lg bg-surface border border-border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-foreground uppercase">{a?.action_type?.replace(/_/g, ' ')}</span>
                <span className="text-xs text-muted-foreground">{new Date(a?.executed_at)?.toLocaleString()}</span>
              </div>
              <p className="text-xs text-muted-foreground">By: <span className="text-foreground">{a?.admin_username || 'System'}</span></p>
              {a?.reason && <p className="text-xs text-muted-foreground mt-1">Reason: {a?.reason}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Full Audit Report Modal ──────────────────────────────────
const AuditReportModal = ({ userId, username, onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const fetch = async () => {
      const { data, error: err } = await supabase?.rpc('admin_audit_user', { p_target_user_id: userId });
      if (err || !data?.success) { setError(err?.message || data?.error); }
      else { setReport(data); }
      setLoading(false);
    };
    fetch();
  }, [userId]);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h2 className="text-lg font-heading font-bold text-foreground">Full Audit Report</h2>
            <p className="text-sm text-muted-foreground">{username} · Generated {report ? new Date(report.generated_at)?.toLocaleString() : '...'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface rounded-lg"><Icon name="X" size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Icon name="Loader" size={32} className="animate-spin text-primary" /></div>
          ) : error ? (
            <p className="text-red-400 text-sm">{error}</p>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="card p-3"><p className="text-xs text-muted-foreground">Cash</p><p className="text-lg font-mono font-bold text-primary">${Number(report?.stats?.cash || 0)?.toLocaleString()}</p></div>
                <div className="card p-3"><p className="text-xs text-muted-foreground">Net Worth</p><p className="text-lg font-mono font-bold text-foreground">${Number(report?.stats?.net_worth || 0)?.toLocaleString()}</p></div>
                <div className="card p-3"><p className="text-xs text-muted-foreground">Level</p><p className="text-lg font-mono font-bold text-foreground">{report?.stats?.level || 1}</p></div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Recent Trades ({report?.recent_trades?.length || 0})</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(report?.recent_trades || [])?.slice(0, 20)?.map((t, i) => (
                    <div key={i} className="flex justify-between text-xs py-1 border-b border-border/30">
                      <span className="text-foreground">{t?.stock_id} · {t?.order_type}</span>
                      <span className="text-muted-foreground">{t?.quantity} @ ${Number(t?.price)?.toFixed(2)}</span>
                      <span className="text-muted-foreground">{new Date(t?.created_at)?.toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Suspicious Activities ({report?.suspicious_activities?.length || 0})</h3>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {(report?.suspicious_activities || [])?.map((s, i) => (
                    <div key={i} className={`flex justify-between text-xs py-1.5 px-2 rounded border ${SEVERITY_COLORS?.[s?.flag_type] || 'bg-surface border-border'}`}>
                      <span className="uppercase">{s?.flag_type?.replace(/_/g, ' ')}</span>
                      <span>Score: {Number(s?.risk_score)?.toFixed(0)}</span>
                      <span>{new Date(s?.created_at)?.toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Admin Actions ({report?.admin_action_history?.length || 0})</h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {(report?.admin_action_history || [])?.map((a, i) => (
                    <div key={i} className="flex justify-between text-xs py-1 border-b border-border/30">
                      <span className="text-foreground uppercase">{a?.action_type?.replace(/_/g, ' ')}</span>
                      <span className="text-muted-foreground">{a?.admin_username}</span>
                      <span className="text-muted-foreground">{new Date(a?.executed_at)?.toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Emergency Confirm Modal ──────────────────────────────────
const EmergencyConfirmModal = ({ control, enabled, totalUsers, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="card p-6 w-full max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="AlertTriangle" size={24} color="#F59E0B" />
          <h2 className="text-lg font-heading font-bold text-foreground">Confirm Emergency Action</h2>
        </div>
        <div className={`p-3 rounded-lg border mb-4 ${control?.bg}`}>
          <p className={`font-semibold ${control?.color}`}>{control?.label}</p>
          <p className="text-xs text-muted-foreground mt-1">{control?.desc}</p>
        </div>
        <p className="text-sm text-warning mb-4">
          ⚠️ This will affect all <span className="font-bold text-foreground">{totalUsers?.toLocaleString()}</span> users.
          Rate limited to 3 emergency actions per 24 hours.
        </p>
        <div className="mb-4">
          <label className="text-xs text-muted-foreground block mb-1">Justification (required)</label>
          <textarea
            value={reason}
            onChange={e => setReason(e?.target?.value)}
            placeholder="Enter reason for this emergency action..."
            className="w-full bg-surface border border-border rounded-lg p-3 text-sm text-foreground resize-none h-20 focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 bg-surface border border-border rounded-md text-sm text-muted-foreground hover:text-foreground">Cancel</button>
          <button
            onClick={() => reason?.trim() && onConfirm(reason)}
            disabled={!reason?.trim()}
            className={`flex-1 py-2 rounded-md text-sm font-semibold border ${control?.bg} ${control?.color} disabled:opacity-40`}
          >
            Execute
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
const AdminPanelPage = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('economy');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Economy state
  const [economyState, setEconomyState] = useState(null);
  const [econFull, setEconFull] = useState(null);
  const [tmsHistory, setTmsHistory] = useState([]);
  const [inflationIndex, setInflationIndex] = useState(0);
  const [lastTickLog, setLastTickLog] = useState(null);

  // Cheat detection
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [alertFilter, setAlertFilter] = useState({ severity: 'ALL', type: 'ALL' });
  const [riskUsers, setRiskUsers] = useState([]);
  const [riskTotal, setRiskTotal] = useState(0);
  const [riskPage, setRiskPage] = useState(0);
  const [riskSearch, setRiskSearch] = useState('');
  const [riskSearchInput, setRiskSearchInput] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [actionMsg, setActionMsg] = useState({});
  const [detailsModal, setDetailsModal] = useState(null);
  const [auditHistModal, setAuditHistModal] = useState(null);
  const [fullAuditModal, setFullAuditModal] = useState(null);

  // Parameter tuning
  const [paramValues, setParamValues] = useState({});
  const [paramLoading, setParamLoading] = useState({});
  const [paramMsg, setParamMsg] = useState({});
  const [paramHistory, setParamHistory] = useState([]);

  // Emergency controls
  const [emergencyLoading, setEmergencyLoading] = useState({});
  const [emergencyModal, setEmergencyModal] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [emergencyActionsToday, setEmergencyActionsToday] = useState(0);

  const refreshRef = useRef(null);
  const RISK_PAGE_SIZE = 50;

  // ── Fetch economy state ──────────────────────────────────────
  const fetchEconomyState = useCallback(async () => {
    try {
      const { data: econData } = await supabase?.from('economy_state')?.select('*')?.limit(1)?.maybeSingle();
      setEconomyState(econData);

      if (econData) {
        setParamValues({
          trading_fee_percentage: Number(econData?.trading_fee_multiplier || 1),
          leverage_cap_multiplier: Number(econData?.leverage_cap || 2),
          crime_base_success_rate: Number(econData?.crime_reward_multiplier || 1) * 10,
          business_operating_cost_multiplier: Number(econData?.operating_cost_multiplier || 1),
          dividend_yield_multiplier: Number(econData?.economy_modifier || 1),
          loan_interest_rate: 5,
          inflation_trigger_threshold: 15,
        });
      }

      // TMS history
      const { data: cashData } = await supabase?.from('player_stats')?.select('cash');
      const tms = cashData?.reduce((s, p) => s + Number(p?.cash || 0), 0) || 0;
      setTmsHistory(prev => {
        const pt = { time: new Date()?.toLocaleTimeString(), tms: Math.round(tms) };
        return [...prev, pt]?.slice(-20);
      });

      // Inflation
      const { data: tickData } = await supabase?.from('tick_execution_log')?.select('*')?.order('executed_at', { ascending: false })?.limit(1);
      setLastTickLog(tickData?.[0] || null);

      // Inflation from economy_state
      if (econData?.inflation_index != null) {
        setInflationIndex(Number(econData?.inflation_index) * 100);
      }

      // Total users
      const { count } = await supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true });
      setTotalUsers(count || 0);

      // Emergency actions today
      const { count: emergCount } = await supabase?.from('admin_actions')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('action_type', 'emergency_control')
        ?.gte('executed_at', new Date(Date.now() - 86400000)?.toISOString());
      setEmergencyActionsToday(emergCount || 0);

    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch cheat detection data ───────────────────────────────
  const fetchCheatData = useCallback(async () => {
    try {
      const { data: alertsData } = await supabase
        ?.from('suspicious_activity')
        ?.select('*, user_profiles(username)')
        ?.order('created_at', { ascending: false })
        ?.limit(50);
      setLiveAlerts(alertsData || []);

      const { data: riskData } = await supabase?.rpc('get_risk_score_users', {
        p_limit: RISK_PAGE_SIZE,
        p_offset: riskPage * RISK_PAGE_SIZE,
        p_min_risk_score: 0,
        p_search: riskSearch || null,
      });
      setRiskUsers(riskData?.users || []);
      setRiskTotal(riskData?.total || 0);
    } catch (err) {
      console.error('Cheat data fetch error:', err);
    }
  }, [riskPage, riskSearch]);

  // ── Fetch param history ──────────────────────────────────────
  const fetchParamHistory = useCallback(async () => {
    const { data } = await supabase
      ?.from('economic_parameter_history')
      ?.select('*, user_profiles(username)')
      ?.order('created_at', { ascending: false })
      ?.limit(20);
    setParamHistory(data || []);
  }, []);

  useEffect(() => {
    fetchEconomyState();
    fetchCheatData();
    fetchParamHistory();
    refreshRef.current = setInterval(() => {
      fetchEconomyState();
    }, 10000);
    return () => clearInterval(refreshRef?.current);
  }, [fetchEconomyState, fetchCheatData, fetchParamHistory]);

  useEffect(() => {
    fetchCheatData();
  }, [fetchCheatData]);

  // ── Real-time subscriptions ──────────────────────────────────
  useRealtimeSubscription(
    'suspicious-activity-admin',
    { table: 'suspicious_activity', event: 'INSERT' },
    useCallback((payload) => {
      if (payload?.new) {
        setLiveAlerts(prev => [payload?.new, ...prev]?.slice(0, 50));
      }
    }, []),
    { enabled: true }
  );

  useRealtimeSubscription(
    'economy-state-admin',
    { table: 'economy_state', event: '*' },
    useCallback(() => { fetchEconomyState(); }, [fetchEconomyState]),
    { enabled: true }
  );

  // ── User actions ─────────────────────────────────────────────
  const handleUserAction = async (userId, action, username) => {
    const key = `${action}_${userId}`;
    setActionLoading(p => ({ ...p, [key]: true }));
    setActionMsg(p => ({ ...p, [key]: null }));
    try {
      let data, error;
      if (action === 'freeze') {
        ({ data, error } = await supabase?.rpc('admin_freeze_account', { p_target_user_id: userId, p_reason: 'Admin panel action' }));
      } else if (action === 'ban') {
        ({ data, error } = await supabase?.rpc('admin_ban_user', { p_target_user_id: userId, p_reason: 'Admin panel ban' }));
      } else if (action === 'clear') {
        ({ data, error } = await supabase?.rpc('admin_clear_flags', { p_target_user_id: userId, p_reason: 'Admin reviewed' }));
      }
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      setActionMsg(p => ({ ...p, [key]: { type: 'success', text: `${action} applied to ${username}` } }));
      fetchCheatData();
    } catch (err) {
      setActionMsg(p => ({ ...p, [key]: { type: 'error', text: err?.message } }));
    } finally {
      setActionLoading(p => ({ ...p, [key]: false }));
    }
  };

  // ── Parameter update ─────────────────────────────────────────
  const handleUpdateParam = async (param) => {
    setParamLoading(p => ({ ...p, [param?.key]: true }));
    setParamMsg(p => ({ ...p, [param?.key]: null }));
    try {
      const { data, error } = await supabase?.rpc('update_economic_parameter', {
        p_parameter_name: param?.dbKey,
        p_new_value: paramValues?.[param?.key],
        p_reason: 'Admin adjusted ' + param?.label,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      const successText = 'Updated: ' + Number(data?.old_value)?.toFixed(3) + ' to ' + Number(data?.new_value)?.toFixed(3);
      setParamMsg(p => ({ ...p, [param?.key]: { type: 'success', text: successText } }));
      fetchParamHistory();
      fetchEconomyState();
    } catch (err) {
      setParamMsg(p => ({ ...p, [param?.key]: { type: 'error', text: err?.message } }));
    } finally {
      setParamLoading(p => ({ ...p, [param?.key]: false }));
    }
  };

  // ── Emergency control ────────────────────────────────────────
  const handleEmergencyControl = async (controlKey, enabled, reason) => {
    setEmergencyLoading(p => ({ ...p, [controlKey]: true }));
    setEmergencyModal(null);
    try {
      const { data, error } = await supabase?.rpc('execute_emergency_control', {
        p_control_type: controlKey,
        p_enabled: enabled,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      fetchEconomyState();
    } catch (err) {
      setError(err?.message);
    } finally {
      setEmergencyLoading(p => ({ ...p, [controlKey]: false }));
    }
  };

  // ── Filtered alerts ──────────────────────────────────────────
  const filteredAlerts = liveAlerts?.filter(a => {
    const typeMatch = alertFilter?.type === 'ALL' || a?.flag_type === alertFilter?.type;
    const sevMatch = alertFilter?.severity === 'ALL' || (
      alertFilter?.severity === 'CRITICAL' ? Number(a?.risk_score) >= 80 :
      alertFilter?.severity === 'HIGH' ? Number(a?.risk_score) >= 60 :
      alertFilter?.severity === 'MEDIUM' ? Number(a?.risk_score) >= 30 :
      Number(a?.risk_score) < 30
    );
    return typeMatch && sevMatch;
  });

  const tabs = [
    { id: 'economy', label: 'Economy State', icon: 'Activity' },
    { id: 'cheat', label: 'Cheat Detection', icon: 'Shield' },
    { id: 'params', label: 'Parameter Tuning', icon: 'Sliders' },
    { id: 'emergency', label: 'Emergency Controls', icon: 'AlertTriangle' },
    { id: 'performance', label: 'Performance', icon: 'Zap' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart2' },
    { id: 'bot-detection', label: 'Bot Detection & Metrics', icon: 'Bot' },
  ];

  if (loading) {
    return (
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} userRole="admin" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">Loading admin panel...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} userRole="admin" />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={28} color="var(--color-primary)" />
              <div>
                <h1 className="text-h2 font-heading text-foreground">Admin Panel</h1>
                <p className="text-sm text-muted-foreground">Real-time economy management · {totalUsers?.toLocaleString()} users</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-xs text-muted-foreground">Live · 10s refresh</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="card p-4 border-red-500/30 mb-4">
              <p className="text-red-400 text-sm">{error}</p>
              <button onClick={() => setError(null)} className="text-xs text-muted-foreground mt-1 hover:text-foreground">Dismiss</button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-surface rounded-lg p-1 overflow-x-auto">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab?.id ? 'bg-primary text-zinc-950' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={14} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* ── TAB: ECONOMY STATE ─────────────────────────────── */}
          {activeTab === 'economy' && (
            <div className="space-y-5">
              {/* Top metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="card p-4">
                  <p className="text-xs text-muted-foreground uppercase mb-1">TMS (Live)</p>
                  <p className="text-xl font-mono font-bold text-primary">
                    ${((tmsHistory?.[tmsHistory?.length - 1]?.tms || 0) / 1000000)?.toFixed(2)}M
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Total Money Supply</p>
                </div>
                <div className="card p-4">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Economy Modifier</p>
                  <p className={`text-xl font-mono font-bold ${Number(economyState?.economy_modifier || 1) < 1 ? 'text-red-400' : 'text-success'}`}>
                    {Number(economyState?.economy_modifier || 1)?.toFixed(2)}x
                  </p>
                </div>
                <div className="card p-4">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Trading Fee</p>
                  <p className="text-xl font-mono font-bold text-foreground">
                    {Number(economyState?.trading_fee_multiplier || 1)?.toFixed(2)}x
                  </p>
                </div>
                <div className="card p-4">
                  <p className="text-xs text-muted-foreground uppercase mb-1">Leverage Cap</p>
                  <p className="text-xl font-mono font-bold text-foreground">
                    {Number(economyState?.leverage_cap || 10)?.toFixed(1)}x
                  </p>
                </div>
              </div>

              {/* Inflation gauge */}
              <div className="card p-5">
                <InflationGauge value={inflationIndex} />
              </div>

              {/* Economy status grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="card p-3 text-center">
                  <p className="text-xs text-muted-foreground">IPO Status</p>
                  <p className={`text-sm font-bold mt-1 ${economyState?.ipo_frozen ? 'text-red-400' : 'text-success'}`}>
                    {economyState?.ipo_frozen ? '🔒 FROZEN' : '✓ ACTIVE'}
                  </p>
                </div>
                <div className="card p-3 text-center">
                  <p className="text-xs text-muted-foreground">Margin Trading</p>
                  <p className={`text-sm font-bold mt-1 ${economyState?.margin_disabled ? 'text-red-400' : 'text-success'}`}>
                    {economyState?.margin_disabled ? '✗ DISABLED' : '✓ ENABLED'}
                  </p>
                </div>
                <div className="card p-3 text-center">
                  <p className="text-xs text-muted-foreground">Recession</p>
                  <p className={`text-sm font-bold mt-1 ${economyState?.recession_state ? 'text-red-400' : 'text-success'}`}>
                    {economyState?.recession_state ? '⚠ ACTIVE' : '✓ NORMAL'}
                  </p>
                </div>
                <div className="card p-3 text-center">
                  <p className="text-xs text-muted-foreground">Tick #</p>
                  <p className="text-sm font-bold mt-1 text-foreground">{economyState?.tick_number || 0}</p>
                </div>
              </div>

              {/* Tick status + countdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Next Tick Countdown</h3>
                  <div className="flex items-center justify-between">
                    <TickCountdown nextTickAt={economyState?.next_tick_at} />
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Last Tick</p>
                      <p className="text-sm font-mono text-foreground">
                        {economyState?.last_tick_at ? new Date(economyState.last_tick_at)?.toLocaleTimeString() : 'Never'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="card p-5">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Last Tick Status</h3>
                  {lastTickLog ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          name={lastTickLog?.tick_status === 'SUCCESS' ? 'CheckCircle' : 'XCircle'}
                          size={18}
                          className={lastTickLog?.tick_status === 'SUCCESS' ? 'text-success' : 'text-red-400'}
                        />
                        <span className={`font-bold ${lastTickLog?.tick_status === 'SUCCESS' ? 'text-success' : 'text-red-400'}`}>
                          {lastTickLog?.tick_status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Duration:</span>
                        <span className={`text-sm font-mono font-bold ${
                          Number(lastTickLog?.tick_duration_ms) > 5000 ? 'text-red-400' :
                          Number(lastTickLog?.tick_duration_ms) > 2000 ? 'text-warning' : 'text-success'
                        }`}>{lastTickLog?.tick_duration_ms}ms</span>
                        {Number(lastTickLog?.tick_duration_ms) > 5000 && (
                          <span className="text-xs text-red-400 font-semibold">⚠ SLOW</span>
                        )}
                      </div>
                      {lastTickLog?.failed_step && (
                        <p className="text-xs text-red-400">Failed step: {lastTickLog?.failed_step}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No tick data yet</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── TAB: CHEAT DETECTION ───────────────────────────── */}
          {activeTab === 'cheat' && (
            <div className="space-y-5">
              {/* Live alerts */}
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase">Live Alerts</h3>
                  <div className="flex gap-2">
                    <select
                      value={alertFilter?.severity}
                      onChange={e => setAlertFilter(p => ({ ...p, severity: e?.target?.value }))}
                      className="bg-surface border border-border rounded px-2 py-1 text-xs text-foreground"
                    >
                      {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW']?.map(s => <option key={s}>{s}</option>)}
                    </select>
                    <select
                      value={alertFilter?.type}
                      onChange={e => setAlertFilter(p => ({ ...p, type: e?.target?.value }))}
                      className="bg-surface border border-border rounded px-2 py-1 text-xs text-foreground"
                    >
                      {['ALL', 'profit_spike', 'wash_trading', 'trade_frequency', 'crime_farming', 'net_worth_slope', 'multi_account', 'margin_abuse', 'vault_siphon', 'bot_precision']?.map(t => (
                        <option key={t} value={t}>{t?.replace(/_/g, ' ')?.toUpperCase()}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {filteredAlerts?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No alerts matching filters</p>
                  ) : filteredAlerts?.map(alert => (
                    <div key={alert?.id} className={`flex items-center justify-between p-3 rounded-lg border ${SEVERITY_COLORS?.[alert?.flag_type] || 'bg-surface border-border'}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground">{alert?.user_profiles?.username || 'Unknown'}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded border ${SEVERITY_COLORS?.[alert?.flag_type] || ''}`}>
                            {alert?.flag_type?.replace(/_/g, ' ')?.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Risk Score: <span className="font-mono text-foreground">{Number(alert?.risk_score)?.toFixed(0)}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-1.5 py-0.5 rounded border ${
                          alert?.flag_status === 'pending' ? 'bg-warning/10 text-warning border-warning/20' :
                          alert?.flag_status === 'cleared' ? 'bg-success/10 text-success border-success/20' : 'bg-red-500/10 text-red-400 border-red-500/20'
                        }`}>{alert?.flag_status?.toUpperCase()}</span>
                        <p className="text-xs text-muted-foreground mt-0.5">{new Date(alert?.created_at)?.toLocaleTimeString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RiskScore user list */}
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase">RiskScore User List ({riskTotal})</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={riskSearchInput}
                      onChange={e => setRiskSearchInput(e?.target?.value)}
                      onKeyDown={e => e?.key === 'Enter' && (setRiskSearch(riskSearchInput), setRiskPage(0))}
                      placeholder="Search username..."
                      className="bg-surface border border-border rounded px-3 py-1 text-xs text-foreground w-40 focus:outline-none focus:border-primary"
                    />
                    <button
                      onClick={() => { setRiskSearch(riskSearchInput); setRiskPage(0); }}
                      className="px-3 py-1 bg-primary text-zinc-950 rounded text-xs font-semibold"
                    >Search</button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-xs text-muted-foreground uppercase">Username</th>
                        <th className="text-right py-2 px-3 text-xs text-muted-foreground uppercase">Risk Score</th>
                        <th className="text-right py-2 px-3 text-xs text-muted-foreground uppercase">Flags</th>
                        <th className="text-right py-2 px-3 text-xs text-muted-foreground uppercase">Last Activity</th>
                        <th className="text-center py-2 px-3 text-xs text-muted-foreground uppercase">Status</th>
                        <th className="text-right py-2 px-3 text-xs text-muted-foreground uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {riskUsers?.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No users with risk score &gt; 0</td></tr>
                      ) : riskUsers?.map(u => {
                        const freezeKey = `freeze_${u?.user_id}`;
                        const banKey = `ban_${u?.user_id}`;
                        const clearKey = `clear_${u?.user_id}`;
                        return (
                          <tr key={u?.user_id} className="border-b border-border/30 hover:bg-surface/50">
                            <td className="py-2 px-3 font-medium text-foreground">{u?.username}</td>
                            <td className="py-2 px-3 text-right">
                              <span className={`font-mono font-bold ${
                                Number(u?.max_risk_score) >= 80 ? 'text-red-400' :
                                Number(u?.max_risk_score) >= 50 ? 'text-orange-400' :
                                Number(u?.max_risk_score) >= 20 ? 'text-warning' : 'text-muted-foreground'
                              }`}>{Number(u?.max_risk_score)?.toFixed(0)}</span>
                            </td>
                            <td className="py-2 px-3 text-right text-muted-foreground">{u?.flagged_count}</td>
                            <td className="py-2 px-3 text-right text-xs text-muted-foreground">
                              {u?.last_activity ? new Date(u.last_activity)?.toLocaleDateString() : '-'}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded border ${
                                u?.status === 'BANNED' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                                u?.status === 'FROZEN' ? 'bg-success/10 text-success border-success/20' : 'bg-success/10 text-success border-success/20'
                              }`}>{u?.status}</span>
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex gap-1 justify-end flex-wrap">
                                <button
                                  onClick={() => setDetailsModal({ userId: u?.user_id, username: u?.username })}
                                  className="text-xs px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20"
                                >View</button>
                                <button
                                  onClick={() => handleUserAction(u?.user_id, 'freeze', u?.username)}
                                  disabled={actionLoading?.[freezeKey] || u?.status === 'BANNED'}
                                  className="text-xs px-2 py-1 rounded bg-warning/10 text-warning border border-warning/20 hover:bg-warning/20 disabled:opacity-40"
                                >{actionLoading?.[freezeKey] ? '...' : u?.account_frozen ? 'Unfreeze' : 'Freeze'}</button>
                                <button
                                  onClick={() => handleUserAction(u?.user_id, 'ban', u?.username)}
                                  disabled={actionLoading?.[banKey] || u?.status === 'BANNED'}
                                  className="text-xs px-2 py-1 rounded bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-40"
                                >{actionLoading?.[banKey] ? '...' : 'Ban'}</button>
                                <button
                                  onClick={() => handleUserAction(u?.user_id, 'clear', u?.username)}
                                  disabled={actionLoading?.[clearKey]}
                                  className="text-xs px-2 py-1 rounded bg-surface border border-border hover:bg-surface/80 text-muted-foreground disabled:opacity-40"
                                >{actionLoading?.[clearKey] ? '...' : 'Clear'}</button>
                                <button
                                  onClick={() => setAuditHistModal({ userId: u?.user_id, username: u?.username })}
                                  className="text-xs px-2 py-1 rounded bg-surface border border-border hover:bg-surface/80 text-muted-foreground"
                                >Audit</button>
                                <button
                                  onClick={() => setFullAuditModal({ userId: u?.user_id, username: u?.username })}
                                  className="text-xs px-2 py-1 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20"
                                >Report</button>
                              </div>
                              {(actionMsg?.[freezeKey] || actionMsg?.[banKey] || actionMsg?.[clearKey]) && (
                                <p className={`text-xs mt-1 ${
                                  (actionMsg?.[freezeKey] || actionMsg?.[banKey] || actionMsg?.[clearKey])?.type === 'success' ? 'text-success' : 'text-red-400'
                                }`}>{(actionMsg?.[freezeKey] || actionMsg?.[banKey] || actionMsg?.[clearKey])?.text}</p>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {riskTotal > RISK_PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-xs text-muted-foreground">
                      Showing {riskPage * RISK_PAGE_SIZE + 1}–{Math.min((riskPage + 1) * RISK_PAGE_SIZE, riskTotal)} of {riskTotal}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setRiskPage(p => Math.max(0, p - 1))}
                        disabled={riskPage === 0}
                        className="px-3 py-1 bg-surface border border-border rounded text-xs text-muted-foreground disabled:opacity-40"
                      >← Prev</button>
                      <button
                        onClick={() => setRiskPage(p => p + 1)}
                        disabled={(riskPage + 1) * RISK_PAGE_SIZE >= riskTotal}
                        className="px-3 py-1 bg-surface border border-border rounded text-xs text-muted-foreground disabled:opacity-40"
                      >Next →</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB: PARAMETER TUNING ──────────────────────────── */}
          {activeTab === 'params' && (
            <div className="space-y-5">
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase">Economic Parameter Controls</h3>
                  <p className="text-xs text-muted-foreground">Max ±20% change per adjustment</p>
                </div>
                <div className="space-y-6">
                  {PARAMS?.map(param => {
                    const currentVal = paramValues?.[param?.key] ?? param?.min;
                    const originalVal = paramValues?.[param?.key] ?? param?.min;
                    const pct = ((currentVal - originalVal) / (originalVal || 1)) * 100;
                    const msg = paramMsg?.[param?.key];
                    return (
                      <div key={param?.key} className="border-b border-border/30 pb-5 last:border-0 last:pb-0">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-foreground">{param?.label}</label>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground">
                              Range: {param?.min}{param?.unit} – {param?.max}{param?.unit}
                            </span>
                            <span className="text-base font-mono font-bold text-primary">
                              {currentVal?.toFixed(param?.step < 1 ? 2 : 1)}{param?.unit}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground w-12 text-right">{param?.min}{param?.unit}</span>
                          <input
                            type="range"
                            min={param?.min}
                            max={param?.max}
                            step={param?.step}
                            value={currentVal}
                            onChange={e => setParamValues(p => ({ ...p, [param?.key]: parseFloat(e?.target?.value) }))}
                            className="flex-1 accent-primary"
                          />
                          <span className="text-xs text-muted-foreground w-12">{param?.max}{param?.unit}</span>
                          <button
                            onClick={() => handleUpdateParam(param)}
                            disabled={paramLoading?.[param?.key]}
                            className="px-4 py-1.5 bg-primary text-zinc-950 rounded text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
                          >
                            {paramLoading?.[param?.key] ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                        {msg && (
                          <p className={`text-xs mt-2 ${msg?.type === 'success' ? 'text-success' : 'text-red-400'}`}>{msg?.text}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Parameter change history */}
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Parameter Change History (Last 20)</h3>
                {paramHistory?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No parameter changes recorded</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-2 text-muted-foreground uppercase">Admin</th>
                          <th className="text-left py-2 px-2 text-muted-foreground uppercase">Parameter</th>
                          <th className="text-right py-2 px-2 text-muted-foreground uppercase">Old</th>
                          <th className="text-right py-2 px-2 text-muted-foreground uppercase">New</th>
                          <th className="text-right py-2 px-2 text-muted-foreground uppercase">Change</th>
                          <th className="text-right py-2 px-2 text-muted-foreground uppercase">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paramHistory?.map(h => (
                          <tr key={h?.id} className="border-b border-border/30 hover:bg-surface/50">
                            <td className="py-2 px-2 text-foreground">{h?.user_profiles?.username || 'Admin'}</td>
                            <td className="py-2 px-2 text-foreground">{h?.parameter_name}</td>
                            <td className="py-2 px-2 text-right font-mono text-muted-foreground">{Number(h?.old_value)?.toFixed(3)}</td>
                            <td className="py-2 px-2 text-right font-mono text-foreground">{Number(h?.new_value)?.toFixed(3)}</td>
                            <td className={`py-2 px-2 text-right font-mono ${
                              Number(h?.change_percentage) > 15 ? 'text-warning' : 'text-muted-foreground'
                            }`}>{Number(h?.change_percentage)?.toFixed(1)}%</td>
                            <td className="py-2 px-2 text-right text-muted-foreground">{new Date(h?.created_at)?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB: EMERGENCY CONTROLS ────────────────────────── */}
          {activeTab === 'emergency' && (
            <div className="space-y-5">
              <div className={`card p-4 border ${
                emergencyActionsToday >= 3 ? 'border-red-500/30 bg-red-500/5' : 'border-warning/30 bg-warning/5'
              }`}>
                <div className="flex items-center gap-2">
                  <Icon name="AlertTriangle" size={16} className={emergencyActionsToday >= 3 ? 'text-red-400' : 'text-warning'} />
                  <p className={`text-sm font-semibold ${emergencyActionsToday >= 3 ? 'text-red-400' : 'text-warning'}`}>
                    Emergency Actions Today: {emergencyActionsToday}/3
                    {emergencyActionsToday >= 3 && ' — RATE LIMIT REACHED'}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">All emergency actions are logged and require justification. Rate limited to 3 per 24 hours.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {EMERGENCY_CONTROLS?.map(ctrl => {
                  const isActive = ctrl?.stateKey ? economyState?.[ctrl?.stateKey] : false;
                  const isLoading = emergencyLoading?.[ctrl?.key];
                  return (
                    <div key={ctrl?.key} className={`card p-5 border ${ctrl?.bg}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${ctrl?.bg}`}>
                            <Icon name={ctrl?.icon} size={20} className={ctrl?.color} />
                          </div>
                          <div>
                            <p className={`font-semibold ${ctrl?.color}`}>{ctrl?.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{ctrl?.desc}</p>
                          </div>
                        </div>
                        {ctrl?.stateKey && (
                          <span className={`text-xs px-2 py-0.5 rounded border ${
                            isActive ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-success/10 text-success border-success/20'
                          }`}>{isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        This will affect all <span className="font-bold text-foreground">{totalUsers?.toLocaleString()}</span> users.
                      </p>
                      <button
                        onClick={() => setEmergencyModal({ control: ctrl, enabled: !isActive })}
                        disabled={isLoading || emergencyActionsToday >= 3}
                        className={`w-full py-2.5 rounded-lg text-sm font-semibold border transition-opacity hover:opacity-80 disabled:opacity-40 ${ctrl?.bg} ${ctrl?.color}`}
                      >
                        {isLoading ? 'Executing...' : isActive ? `Disable ${ctrl?.label}` : `Execute: ${ctrl?.label}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'performance' && <PerformanceDashboard />}
          {activeTab === 'analytics' && <AnalyticsDashboard />}
          {activeTab === 'bot-detection' && <BotDetectionTab />}
        </div>
      </main>
      {/* Modals */}
      {detailsModal && (
        <UserDetailsModal
          userId={detailsModal?.userId}
          username={detailsModal?.username}
          onClose={() => setDetailsModal(null)}
        />
      )}
      {auditHistModal && (
        <AuditHistoryModal
          userId={auditHistModal?.userId}
          username={auditHistModal?.username}
          onClose={() => setAuditHistModal(null)}
        />
      )}
      {fullAuditModal && (
        <AuditReportModal
          userId={fullAuditModal?.userId}
          username={fullAuditModal?.username}
          onClose={() => setFullAuditModal(null)}
        />
      )}
      {emergencyModal && (
        <EmergencyConfirmModal
          control={emergencyModal?.control}
          enabled={emergencyModal?.enabled}
          totalUsers={totalUsers}
          onConfirm={(reason) => handleEmergencyControl(emergencyModal?.control?.key, emergencyModal?.enabled, reason)}
          onClose={() => setEmergencyModal(null)}
        />
      )}
    </div>
  );
};

export default AdminPanelPage;
